/**
 * SubmissionDetailModal - View submission details with PHI decryption
 * 
 * HIPAA COMPLIANCE:
 * - Shows metadata and field structure by default (no PHI)
 * - PHI decryption requires MFA verification
 * - Decrypted data is kept in memory only
 * - Every PHI access is logged to audit trail
 */

import { useState, useEffect } from 'react';
import type {
  SubmissionDetail,
  DecryptedSubmissionData,
  SubmissionFieldDefinition,
} from '@formflow/shared';
import { submissionsApi, ApiClientError } from '../../lib/api';
import './SubmissionDetailModal.css';

interface SubmissionDetailModalProps {
  submissionId: string;
  onClose: () => void;
}

// Format date for display
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function SubmissionDetailModal({ submissionId, onClose }: SubmissionDetailModalProps) {
  // State
  const [submission, setSubmission] = useState<SubmissionDetail | null>(null);
  const [decryptedData, setDecryptedData] = useState<DecryptedSubmissionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // MFA state
  const [showMfaPrompt, setShowMfaPrompt] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [decrypting, setDecrypting] = useState(false);

  // Load submission detail
  useEffect(() => {
    async function loadSubmission() {
      setLoading(true);
      setError(null);

      try {
        const data = await submissionsApi.get(submissionId);
        setSubmission(data);
      } catch (err) {
        if (err instanceof ApiClientError) {
          setError(err.message);
        } else {
          setError('Failed to load submission');
        }
      } finally {
        setLoading(false);
      }
    }

    loadSubmission();
  }, [submissionId]);

  // Handle MFA verification and decryption
  const handleDecrypt = async () => {
    if (!mfaCode || mfaCode.length !== 6) {
      setMfaError('Please enter a 6-digit MFA code');
      return;
    }

    setDecrypting(true);
    setMfaError(null);

    try {
      const data = await submissionsApi.decrypt(submissionId, { mfa_code: mfaCode });
      setDecryptedData(data);
      setShowMfaPrompt(false);
      setMfaCode('');
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.isForbidden) {
          setMfaError('Invalid MFA code. Please try again.');
        } else {
          setMfaError(err.message);
        }
      } else {
        setMfaError('Failed to decrypt data');
      }
    } finally {
      setDecrypting(false);
    }
  };

  // Get the value for a field (either decrypted or placeholder)
  const getFieldValue = (fieldDef: SubmissionFieldDefinition): { value: string; isEncrypted: boolean } => {
    if (decryptedData) {
      const field = decryptedData.fields.find(f => f.name === fieldDef.name);
      if (field) {
        return { value: String(field.value ?? ''), isEncrypted: false };
      }
    }
    
    if (fieldDef.is_phi) {
      return { value: '[ENCRYPTED]', isEncrypted: true };
    }
    
    return { value: '[Not submitted]', isEncrypted: false };
  };

  // Handle close - clear decrypted data from memory
  const handleClose = () => {
    // Clear decrypted data from memory for security
    setDecryptedData(null);
    onClose();
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="submission-detail-modal">
        {/* Header */}
        <header className="modal-header">
          <h2>Submission Details</h2>
          <button className="btn btn-icon close-btn" onClick={handleClose}>
            ✕
          </button>
        </header>

        {/* Loading State */}
        {loading && (
          <div className="modal-loading">
            <div className="spinner" />
            <p>Loading submission...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="modal-error">
            <p>{error}</p>
            <button className="btn btn-primary" onClick={handleClose}>
              Close
            </button>
          </div>
        )}

        {/* Content */}
        {!loading && !error && submission && (
          <div className="modal-content">
            {/* Metadata Section */}
            <section className="detail-section">
              <h3>Submission Info</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Status</span>
                  <span className={`info-value status-${submission.status}`}>
                    {submission.status}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Progress</span>
                  <span className="info-value">
                    {submission.current_step}/{submission.total_steps} steps ({submission.progress_percentage}%)
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Started</span>
                  <span className="info-value">{formatDate(submission.started_at)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Completed</span>
                  <span className="info-value">
                    {submission.completed_at ? formatDate(submission.completed_at) : '—'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Device</span>
                  <span className="info-value">{submission.device_category || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Referrer</span>
                  <span className="info-value">{submission.referrer_domain || '—'}</span>
                </div>
              </div>
            </section>

            {/* PHI Notice */}
            {submission.data_summary.has_phi_fields && !decryptedData && (
              <section className="phi-notice">
                <div className="notice-icon">🔒</div>
                <div className="notice-content">
                  <h4>Protected Health Information</h4>
                  <p>
                    This submission contains {submission.data_summary.phi_field_count} PHI field(s).
                    MFA verification is required to view the data.
                  </p>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowMfaPrompt(true)}
                  >
                    Unlock PHI Data
                  </button>
                </div>
              </section>
            )}

            {/* Decryption success notice */}
            {decryptedData && (
              <section className="decryption-notice">
                <div className="notice-icon">✓</div>
                <div className="notice-content">
                  <p>
                    PHI data decrypted at {formatDate(decryptedData.decrypted_at)}.
                    This access has been logged to the audit trail.
                  </p>
                </div>
              </section>
            )}

            {/* Field Values */}
            <section className="detail-section">
              <h3>Submitted Data</h3>
              <div className="fields-list">
                {submission.field_definitions.map((field) => {
                  const { value, isEncrypted } = getFieldValue(field);
                  return (
                    <div key={field.id} className="field-row">
                      <div className="field-header">
                        <span className="field-label">{field.label}</span>
                        {field.is_phi && (
                          <span className="phi-badge" title="Protected Health Information">
                            PHI
                          </span>
                        )}
                      </div>
                      <div className={`field-value ${isEncrypted ? 'encrypted' : ''}`}>
                        {isEncrypted ? (
                          <span className="encrypted-placeholder">
                            🔒 {value}
                          </span>
                        ) : (
                          value || <span className="empty-value">—</span>
                        )}
                      </div>
                      <div className="field-meta">
                        <span className="field-type">{field.field_type}</span>
                        <span className="field-step">{field.step_name}</span>
                      </div>
                    </div>
                  );
                })}

                {submission.field_definitions.length === 0 && (
                  <p className="no-fields">No field data available</p>
                )}
              </div>
            </section>

            {/* Step Completions */}
            {submission.step_completions.length > 0 && (
              <section className="detail-section">
                <h3>Step Progress</h3>
                <div className="steps-timeline">
                  {submission.step_completions.map((step) => (
                    <div
                      key={step.id}
                      className={`timeline-item ${step.is_complete ? 'complete' : 'incomplete'} ${step.was_skipped ? 'skipped' : ''}`}
                    >
                      <div className="timeline-marker">
                        {step.is_complete ? '✓' : step.was_skipped ? '—' : '○'}
                      </div>
                      <div className="timeline-content">
                        <span className="step-name">{step.step_name}</span>
                        <span className="step-variant">Variant: {step.variant_name}</span>
                        {step.time_spent_seconds && (
                          <span className="step-time">
                            {Math.round(step.time_spent_seconds / 60)}m {step.time_spent_seconds % 60}s
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {/* Footer */}
        <footer className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose}>
            Close
          </button>
        </footer>

        {/* MFA Prompt Overlay */}
        {showMfaPrompt && (
          <div className="mfa-overlay">
            <div className="mfa-prompt">
              <h3>🔐 MFA Verification Required</h3>
              <p>
                Enter your 6-digit authenticator code to decrypt and view PHI data.
                This access will be logged to the audit trail.
              </p>
              
              <div className="mfa-input-group">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={mfaCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setMfaCode(value);
                    setMfaError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleDecrypt();
                    }
                  }}
                  autoFocus
                  disabled={decrypting}
                />
              </div>

              {mfaError && (
                <p className="mfa-error">{mfaError}</p>
              )}

              <div className="mfa-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowMfaPrompt(false);
                    setMfaCode('');
                    setMfaError(null);
                  }}
                  disabled={decrypting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleDecrypt}
                  disabled={decrypting || mfaCode.length !== 6}
                >
                  {decrypting ? 'Verifying...' : 'Verify & Decrypt'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
